"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Workbench } from "@/components/ui/workbench";
import { useSetWindowTitle } from "@/components/ui/os-layout";
import * as GuardiansV2API from "@/services/api/guardiansv2";
import {
  clearSecondaryFilter,
  type GuardiansFilterValues,
} from "./shared/GuardiansFilterBar";
import {
  GuardiansToolbarActions,
  type GuardiansToolbarSlotConfig,
} from "./shared/GuardiansToolbarActions";
import { GuardiansActiveFilterChips } from "./shared/GuardiansActiveFilterChips";
import { useGuardiansFilterOptions } from "./shared/useGuardiansFilterOptions";
import { AuditInspector } from "./audits/AuditInspector";
import { useAudits } from "./audits/useAudits";
import { useScopeLabelCache } from "./audits/useScopeLabelCache";
import { SettingsDrawer } from "./settings/SettingsDrawer";
import { useAlerts } from "./alerts/useAlerts";
import { useSelectedAlert } from "./alerts/useSelectedAlert";
import { AlertsPane } from "./alerts/AlertsPane";
import { AlertPanel } from "./alerts/AlertPanel";
import {
  pendingAuditForAlert,
  pruneCompletedPendingAudits,
  type PendingAlertAuditMap,
} from "./alerts/pendingAlertAudit";
import { entityRefFromAudit } from "./shared/entityDeepLink";
import { ViewEntityButton } from "./preview/ViewEntityButton";
import { AuditsView } from "./views/AuditsView";
import { NewAuditDialog, NewAuditButton } from "./audits/NewAuditDialog";
import { RulesPane } from "./rules/RulesPane";
import { useRules } from "./rules/useRules";
import {
  filtersFromSearchParams,
  parseAuditBucket,
  parseAuditOriginFilter,
  normalizeGuardiansScreeningParam,
  parseScreeningBucket,
  parseStatsTimePeriod,
  type GuardiansView,
} from "./guardiansParams";
import {
  guardiansCodec,
  guardiansParamPatch,
  guardiansShareUrl,
} from "@/navigation/apps/guardiansLocation";
import { useAppLocation } from "@/navigation/appLocation";
import { ConversationView } from "./conversation/ConversationView";
import { StatsView } from "./stats/StatsView";
import { StatsToolbarControls } from "./stats/StatsToolbarControls";
import type { ActiveFilterChipId, GuardiansTabFilterState } from "./shared/guardiansViewFilters";
import { resolveGuardiansLayout } from "./guardiansLayout";
import { GuardiansAuditLaunchProvider, useGuardiansAuditLaunch } from "./auditLaunch/GuardiansAuditLaunchContext";
import { useScreenings } from "./screenings/useScreenings";
import { ScreeningInspector } from "./screenings/ScreeningInspector";
import { ScreeningsPane } from "./screenings/ScreeningsPane";
import { PullToRefresh } from "@/components/ui/pull-to-refresh";

export type { GuardiansView };

const VIEW_TABS: Array<{ id: GuardiansView; label: string }> = [
  { id: "alerts", label: "Alerts" },
  { id: "audits", label: "Audits" },
  { id: "screenings", label: "Screenings" },
  { id: "rules", label: "Rules" },
  { id: "stats", label: "Stats" },
];

const SEARCH_PLACEHOLDERS: Record<
  Exclude<GuardiansView, "conversation" | "stats">,
  string
> = {
  alerts: "Search alerts…",
  audits: "Search audits…",
  screenings: "Search screenings…",
  rules: "Search task types…",
};

export function GuardiansApp() {
  return (
    <GuardiansAuditLaunchProvider>
      <GuardiansAppContent />
    </GuardiansAuditLaunchProvider>
  );
}

function GuardiansAppContent() {
  const { registerLaunches, getSingleAuditProgress, dismissLaunches } = useGuardiansAuditLaunch();
  const [location, setLocation] = useAppLocation(guardiansCodec);
  const setWindowTitle = useSetWindowTitle();
  const filterOptions = useGuardiansFilterOptions();

  const view = location.view ?? "alerts";
  const locationSearchParams = useMemo(
    () => new URLSearchParams(guardiansCodec.toParams(location)),
    [location],
  );
  const filters = useMemo(
    () => filtersFromSearchParams(locationSearchParams),
    [locationSearchParams],
  );
  const selectedAuditId = location.auditId ?? null;
  const selectedAlertId = location.alertId ?? null;
  const catalogueKeyFromUrl = location.catalogueKey ?? null;
  const statsTimePeriod = parseStatsTimePeriod(location.timePeriod ?? null);
  const highlightRuleId = location.ruleId ?? null;
  const bucket = parseAuditBucket(location.rating ?? null);
  const auditOrigin = parseAuditOriginFilter(location.auditOrigin ?? null);
  const rawScreeningParam =
    location.screeningId ?? location.screeningOutcome ?? locationSearchParams.get("screening");
  const normalizedScreening = useMemo(
    () => normalizeGuardiansScreeningParam(rawScreeningParam),
    [rawScreeningParam],
  );
  const selectedScreeningId = normalizedScreening.screeningId;
  const screeningBucket = parseScreeningBucket(location.screeningOutcome ?? null);

  const [search, setSearch] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(() => location.settings != null);
  const [newAuditOpen, setNewAuditOpen] = useState(false);
  const [pendingAlertAudits, setPendingAlertAudits] = useState<PendingAlertAuditMap>({});
  /** Audit id queued from Investigate per alert — kept after the runner finishes. */
  const [linkedAuditIdsByAlert, setLinkedAuditIdsByAlert] = useState<Record<string, string>>({});

  useEffect(() => {
    setWindowTitle("The Guardians");
  }, [setWindowTitle]);

  useEffect(() => {
    if (location.settings) setSettingsOpen(true);
  }, [location.settings]);

  const listScrollRef = useRef<HTMLElement>(null);

  const patchParams = useCallback(
    (patch: Record<string, string | null>) => {
      setLocation(guardiansParamPatch(location, patch));
    },
    [location, setLocation],
  );

  useEffect(() => {
    if (location.view === "audits" && location.auditsMode === "screenings") {
      patchParams({ view: "screenings", audits_mode: null });
    }
  }, [location.view, location.auditsMode, patchParams]);

  const screeningOutcomeParam = location.screeningOutcome ?? null;

  useEffect(() => {
    if (view !== "screenings" || rawScreeningParam == null) return;
    const { screeningId, screeningOutcomePatch } = normalizedScreening;
    const patch: Record<string, string | null> = {};
    if (rawScreeningParam !== screeningId) {
      patch.screening = screeningId;
    }
    if (screeningOutcomePatch != null && screeningOutcomeParam !== screeningOutcomePatch) {
      patch.screening_outcome = screeningOutcomePatch;
    }
    if (Object.keys(patch).length > 0) {
      patchParams(patch);
    }
  }, [view, rawScreeningParam, normalizedScreening, screeningOutcomeParam, patchParams]);

  const auditsState = useAudits({
    filters,
    bucket,
    search,
    auditOrigin,
    selectedAuditId: view === "audits" ? selectedAuditId : null,
    enabled: view === "audits",
  });
  const screeningsState = useScreenings({
    filters,
    bucket: screeningBucket,
    search,
    selectedScreeningId: view === "screenings" ? selectedScreeningId : null,
    enabled: view === "screenings",
  });
  const urlMissionId = location.missionId ?? null;
  const alertsState = useAlerts({
    enabled: view === "alerts" || view === "conversation",
    missionId: urlMissionId || null,
    templateId: filters.missionTemplateId || null,
    taskId: filters.taskId || null,
    agentId: filters.agentId || null,
    catalogueId: filters.catalogueId || null,
    category: filters.alertCategory !== "all" ? filters.alertCategory : null,
    audience: filters.alertAudience !== "all" ? filters.alertAudience : null,
  });
  const rulesState = useRules(view === "rules" || view === "stats");
  const { setCatalogueKey: setRulesCatalogueKey } = rulesState;
  const { archive: archiveAlert } = alertsState;

  useEffect(() => {
    if (view === "rules" && catalogueKeyFromUrl) {
      setRulesCatalogueKey(catalogueKeyFromUrl);
    }
  }, [view, catalogueKeyFromUrl, setRulesCatalogueKey]);

  const handleEditRule = useCallback(
    (catalogueKey: string, ruleId: string) => {
      patchParams({
        view: "rules",
        catalogue_key: catalogueKey,
        rule_id: ruleId,
        alert: null,
        audit: null,
      });
    },
    [patchParams]
  );
  const editRuleHandler = rulesState.canEditRules ? handleEditRule : undefined;

  const { alert: selectedAlert, loading: selectedAlertLoading } = useSelectedAlert(
    view === "alerts" ? selectedAlertId : null,
    alertsState.alerts
  );

  const scopeLabels = useScopeLabelCache(
    view === "audits" ? auditsState.audits : [],
    view === "alerts" || view === "conversation" ? alertsState.alerts : []
  );

  const showAlertPanel = view === "alerts" && selectedAlertId != null;
  const conversationMissionId = view === "conversation" ? location.missionId ?? null : null;
  const conversationTaskId = view === "conversation" ? location.taskId ?? null : null;
  const conversationFocus =
    view === "conversation" ? locationSearchParams.get("focus") : null;
  const layout = resolveGuardiansLayout({
    view,
    selectedAuditId,
    selectedScreeningId,
    showAlertPanel,
  });

  const tabFilters = useMemo(
    (): GuardiansTabFilterState => ({
      auditBucket: bucket,
      auditOrigin,
      screeningBucket,
    }),
    [bucket, auditOrigin, screeningBucket]
  );

  const patchTabFilterUrl = useCallback(
    (patch: { rating?: string | null; audit_origin?: string | null; screening_outcome?: string | null }) => {
      patchParams(patch);
    },
    [patchParams]
  );

  const patchFilters = useCallback(
    (next: GuardiansFilterValues) => {
      patchParams({
        audit_type: next.auditType === "all" ? null : next.auditType,
        mission_template: next.missionTemplateId || null,
        task: next.taskId || null,
        agent: next.agentId || null,
        catalogue: next.catalogueId || null,
        category: next.alertCategory === "all" ? null : next.alertCategory,
        audience: next.alertAudience === "all" ? null : next.alertAudience,
      });
    },
    [patchParams]
  );

  const handleRemoveFilterChip = useCallback(
    (id: ActiveFilterChipId) => {
      if (id === "auditBucket") {
        patchParams({ rating: null });
        return;
      }
      if (id === "auditOrigin") {
        patchParams({ audit_origin: null });
        return;
      }
      if (id === "screeningBucket") {
        patchParams({ screening_outcome: null });
        return;
      }
      patchFilters(clearSecondaryFilter(filters, id));
    },
    [filters, patchFilters, patchParams]
  );

  const visibleAlerts = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = alertsState.alerts;
    if (!q) return rows;
    return rows.filter(
      (alert) =>
        (alert.title ?? "").toLowerCase().includes(q) ||
        (alert.summary ?? "").toLowerCase().includes(q) ||
        (alert.description ?? "").toLowerCase().includes(q)
    );
  }, [alertsState.alerts, search]);

  const toolbarSlot = useMemo((): GuardiansToolbarSlotConfig | null => {
    if (view === "audits") {
      return {
        visible: true,
        content: <NewAuditButton onClick={() => setNewAuditOpen(true)} />,
      };
    }
    return null;
  }, [view]);

  const handleAuditAlertIssue = async (alertId: string, userFocus?: string) => {
    const result = await GuardiansV2API.auditFromAlert(alertId, {
      user_focus: userFocus,
    });
    if (result.audit_id && result.task_id) {
      registerLaunches([
        { taskId: result.task_id, auditId: result.audit_id, kind: "single" },
      ]);
      setPendingAlertAudits((prev) => ({
        ...prev,
        [alertId]: { auditId: result.audit_id, taskId: result.task_id },
      }));
      setLinkedAuditIdsByAlert((prev) => ({
        ...prev,
        [alertId]: result.audit_id,
      }));
    }
  };

  const handleAuditScreening = async (screeningId: string, userFocus?: string) => {
    const result = await GuardiansV2API.auditFromScreening(screeningId, {
      user_focus: userFocus,
    });
    if (result.audit_id && result.task_id) {
      registerLaunches([
        { taskId: result.task_id, auditId: result.audit_id, kind: "single" },
      ]);
      patchParams({ view: "audits", audit: result.audit_id });
    }
  };

  const handleArchiveAlert = useCallback(
    (alertId: string) => {
      if (selectedAlertId === alertId) {
        patchParams({ alert: null });
      }
      void archiveAlert(alertId);
    },
    [archiveAlert, selectedAlertId, patchParams]
  );

  const selectedPendingAudit = pendingAuditForAlert(pendingAlertAudits, selectedAlert?._id);

  const alertIssueProgress = selectedPendingAudit
    ? getSingleAuditProgress(selectedPendingAudit.taskId)
    : undefined;

  const linkedAuditIdForPanel =
    selectedPendingAudit?.auditId ??
    (selectedAlert ? linkedAuditIdsByAlert[selectedAlert._id] : undefined) ??
    selectedAlert?.audit_id ??
    null;

  useEffect(() => {
    const { next, completedTaskIds } = pruneCompletedPendingAudits(
      pendingAlertAudits,
      (taskId) => getSingleAuditProgress(taskId)
    );
    if (completedTaskIds.length > 0) {
      dismissLaunches(completedTaskIds);
    }
    if (next !== pendingAlertAudits) {
      setPendingAlertAudits(next);
    }
  }, [pendingAlertAudits, getSingleAuditProgress, dismissLaunches]);

  useEffect(() => {
    if (view !== "audits" || !selectedAuditId) return;
    if (auditsState.missingAuditId !== selectedAuditId) return;
    patchParams({ audit: null });
  }, [view, selectedAuditId, auditsState.missingAuditId, patchParams]);

  useEffect(() => {
    if (view !== "screenings" || !selectedScreeningId) return;
    if (screeningsState.missingScreeningId !== selectedScreeningId) return;
    patchParams({ screening: null });
  }, [view, selectedScreeningId, screeningsState.missingScreeningId, patchParams]);

  const auditForInspectorFooter =
    layout.showAuditsInspector ? auditsState.selectedAudit : null;

  const auditInspectorFooter = useMemo(() => {
    if (!auditForInspectorFooter) return null;
    const ref = entityRefFromAudit(auditForInspectorFooter);
    if (!ref) return null;
    return <ViewEntityButton entity={ref} variant="footer" previewPlacement="top" />;
  }, [auditForInspectorFooter]);

  const filterChips = (
    <GuardiansActiveFilterChips
      view={view}
      values={filters}
      tabFilters={tabFilters}
      missionTemplates={filterOptions.missionTemplates}
      agents={filterOptions.agents}
      catalogues={filterOptions.catalogues}
      scopeLabels={scopeLabels}
      onRemove={handleRemoveFilterChip}
    />
  );

  const statsFilters = useMemo(
    () => ({
      time_period: statsTimePeriod,
      task_id: filters.taskId || null,
    }),
    [statsTimePeriod, filters.taskId]
  );

  const secondary =
    view === "stats" ? (
      <StatsToolbarControls
        timePeriod={statsTimePeriod}
        onTimePeriodChange={(period) => patchParams({ time_period: period === "1m" ? null : period })}
      />
    ) : null;

  const panels = showAlertPanel || layout.showAuditsInspector || layout.showScreeningsInspector ? (
    <>
      {showAlertPanel ? (
        <aside
          className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-2xl bg-slate-700"
          data-testid="guardians-alert-panel"
        >
          {selectedAlert ? (
            <AlertPanel
              alert={selectedAlert}
              scopeLabels={scopeLabels}
              shareUrl={guardiansShareUrl({ alert: selectedAlert._id })}
              onClose={() => patchParams({ alert: null })}
              onArchive={() => handleArchiveAlert(selectedAlert._id)}
              onAuditIssue={(userFocus) => void handleAuditAlertIssue(selectedAlert._id, userFocus)}
              auditIssueProgress={alertIssueProgress}
              linkedAuditId={linkedAuditIdForPanel}
              onEditRule={editRuleHandler}
            />
          ) : selectedAlertLoading ? (
            <p className="p-6 text-sm text-slate-400">Loading…</p>
          ) : null}
        </aside>
      ) : null}

      {layout.showAuditsInspector ? (
        <aside className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-2xl bg-slate-700">
          <AuditInspector
            audit={auditsState.selectedAudit}
            loading={auditsState.selectedLoading}
            shareUrl={
              selectedAuditId
                ? guardiansShareUrl({ view: "audits", audit: selectedAuditId })
                : undefined
            }
            footer={auditInspectorFooter}
            onClose={() => patchParams({ audit: null })}
            onRefresh={auditsState.refreshSelected}
            onRerunComplete={auditsState.refresh}
            scopeLabels={scopeLabels}
            onEditRule={editRuleHandler}
          />
        </aside>
      ) : null}
      {layout.showScreeningsInspector ? (
        <aside className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-2xl bg-slate-700">
          <ScreeningInspector
            screening={screeningsState.selectedScreening}
            loading={screeningsState.selectedLoading}
            shareUrl={
              selectedScreeningId
                ? guardiansShareUrl({
                    view: "screenings",
                    screening: selectedScreeningId,
                  })
                : undefined
            }
            onClose={() => patchParams({ screening: null })}
            scopeLabels={scopeLabels}
            onOpenAlert={(alertId) =>
              patchParams({ view: null, alert: alertId, screening: null })
            }
            onInvestigate={(userFocus) =>
              selectedScreeningId
                ? void handleAuditScreening(selectedScreeningId, userFocus)
                : undefined
            }
          />
        </aside>
      ) : null}
    </>
  ) : undefined;

  const filteredCatalogue =
    view === "rules" && search.trim()
      ? rulesState.catalogue.filter((c) =>
          c.display_name.toLowerCase().includes(search.trim().toLowerCase())
        )
      : rulesState.catalogue;

  return (
    <>
      <Workbench
        data-testid="guardians-app"
        tabs={VIEW_TABS}
        activeTab={view === "conversation" ? "alerts" : view}
        tabTestIdPrefix="guardians-view-tab"
        onTabChange={(id) =>
          patchParams({
            view: id === "alerts" ? null : id,
            audit: id === "audits" ? selectedAuditId : null,
            screening: id === "screenings" ? selectedScreeningId : null,
            alert: null,
            conversation: null,
            audits_mode: null,
          })
        }
        toolbarCenter={filterChips}
        actions={
          <GuardiansToolbarActions
            view={view}
            filters={filters}
            tabFilters={tabFilters}
            filterOptions={filterOptions}
            onFiltersChange={patchFilters}
            onAuditBucketChange={(next) => patchParams({ rating: next === "all" ? null : next })}
            onAuditOriginChange={(next) =>
              patchParams({ audit_origin: next === "all" ? null : next })
            }
            onScreeningBucketChange={(next) =>
              patchParams({ screening_outcome: next === "all" ? null : next })
            }
            onTabFilterPatch={patchTabFilterUrl}
            onOpenSettings={() => setSettingsOpen(true)}
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder={
              view === "conversation"
                ? SEARCH_PLACEHOLDERS.alerts
                : view === "stats"
                  ? undefined
                  : SEARCH_PLACEHOLDERS[view]
            }
            showSearch={view !== "stats" && view !== "rules"}
            slot={toolbarSlot}
          />
        }
        secondary={secondary}
        mainColumnClassName={layout.mainColumnClass}
        panels={panels}
      >
        {view === "alerts" ? (
          <section ref={listScrollRef} className="min-h-0 flex-1 overflow-y-auto">
            <AlertsPane
              alerts={visibleAlerts}
              loading={alertsState.loading}
              loadingMore={alertsState.loadingMore}
              error={alertsState.error}
              hasMore={alertsState.hasMore}
              onLoadMore={alertsState.loadMore}
              scopeLabels={scopeLabels}
              selectedAlertId={selectedAlertId}
              onSelect={(id) => patchParams({ alert: id })}
              onArchive={handleArchiveAlert}
            />
          </section>
        ) : null}

        {view === "audits" ? (
          <AuditsView
            auditsState={auditsState}
            selectedAuditId={selectedAuditId}
            scopeLabels={scopeLabels}
            listScrollRef={listScrollRef}
            onSelectAudit={(id) => patchParams({ audit: id, screening: null })}
          />
        ) : null}

        {view === "screenings" ? (
          <PullToRefresh
            scrollRef={listScrollRef}
            onRefresh={screeningsState.refresh}
            className="min-h-0 flex-1"
            data-testid="screenings-pull-to-refresh"
          >
            <ScreeningsPane
              screenings={screeningsState.screenings}
              loading={screeningsState.loading}
              loadingMore={screeningsState.loadingMore}
              error={screeningsState.error}
              selectedId={selectedScreeningId}
              onSelect={(id) => patchParams({ screening: id, audit: null })}
              hasMore={screeningsState.hasMore}
              onLoadMore={screeningsState.loadMore}
              scrollRootRef={listScrollRef}
            />
          </PullToRefresh>
        ) : null}

        {view === "rules" ? (
          <RulesPane
            rulesState={{
              ...rulesState,
              catalogue: filteredCatalogue,
            }}
            highlightRuleId={highlightRuleId}
          />
        ) : null}

        {view === "conversation" ? (
          <ConversationView
            missionId={conversationMissionId}
            taskId={conversationTaskId}
            focus={conversationFocus}
            scopeLabels={scopeLabels}
            onBack={() =>
              patchParams({
                view: null,
                conversation: null,
                focus: null,
              })
            }
          />
        ) : null}

        {view === "stats" ? <StatsView filters={statsFilters} /> : null}
      </Workbench>

      <NewAuditDialog
        open={newAuditOpen}
        onClose={() => setNewAuditOpen(false)}
        onQueued={(launches) => {
          registerLaunches(
            launches.map((launch) => ({
              taskId: launch.task_id,
              auditId: launch.audit_id,
              kind: "batch",
            }))
          );
          auditsState.refresh();
        }}
      />
      <SettingsDrawer open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </>
  );
}
